"use client";

import {
	Check,
	ChevronDown,
	Cloud,
	Download,
	FileAudio,
	FileImage,
	FileVideo,
	FolderOpen,
	HardDrive,
	Link2,
	Loader2,
	Search,
	Settings2,
	ShieldCheck,
	Trash2,
	UploadCloud,
	X,
} from "lucide-react";
import {
	useEffect,
	useMemo,
	useRef,
	useState,
	type ChangeEvent,
	type DragEvent,
	type ReactNode,
} from "react";
import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { REMOTE_VIDEO_SOURCES_ENABLED } from "@/components/tools/video-converter-config";
import {
	importDropboxVideos,
	importGoogleDriveVideos,
	importOneDriveVideos,
	importVideoFromUrl,
	preloadCloudSourceSdks,
	type ImportedVideo,
} from "@/components/tools/video-source-import";
import { cn } from "@/utils/ui";
// FFmpeg will be loaded dynamically on client side only

// Types for conversion options
interface ConversionOptions {
	format: string;
	videoCodec?: string;
	audioCodec?: string;
	quality?: string;
	resolution?: string;
	frameRate?: string;
	keepAudio?: boolean;
	bitrate?: string;
	sampleRate?: string;
	channels?: string;
	gifSize?: string;
	gifFrameRate?: string;
	loop?: string;
}

const ACCEPTED_FILE_TYPES =
	"video/*,.mkv,.m4v,.mts,.m2ts,.ts,.ogv,.wmv,.flv,.3gp,.3g2";

type SourceKind = "device" | "dropbox" | "google-drive" | "onedrive" | "url";
type CloudSource = Exclude<SourceKind, "device" | "url">;
type OutputCategory = "video" | "audio" | "image";

type ConversionStatus =
	| "idle"
	| "loading"
	| "converting"
	| "completed"
	| "failed";

interface QueueItem {
	id: string;
	name: string;
	size?: number;
	source: SourceKind;
	sourceLabel: string;
	file?: File;
	status: ConversionStatus;
	progress?: number;
	error?: string;
	outputBlob?: Blob;
}

const OUTPUT_FORMATS: Record<OutputCategory, readonly string[]> = {
	video: [
		"MP4",
		"MOV",
		"MKV",
		"WebM",
		"AVI",
		"M4V",
		"FLV",
		"WMV",
		"3GP",
		"OGV",
		"MPEG",
		"TS",
	],
	audio: ["MP3", "M4A", "WAV", "AAC", "FLAC", "OGG", "Opus", "AIFF"],
	image: ["GIF"],
};

function formatBytes(bytes: number): string {
	if (bytes === 0) return "0 B";
	const units = ["B", "KB", "MB", "GB"];
	const unitIndex = Math.min(
		Math.floor(Math.log(bytes) / Math.log(1024)),
		units.length - 1,
	);
	return `${(bytes / 1024 ** unitIndex).toFixed(unitIndex > 1 ? 1 : 0)} ${units[unitIndex]}`;
}

function SettingSelect({
	label,
	value,
	onChange,
	children,
}: {
	label: string;
	value?: string;
	onChange?: (value: string) => void;
	children: ReactNode;
}) {
	return (
		<label className="block">
			<span className="mb-2 block text-xs font-bold tracking-wide uppercase">
				{label}
			</span>
			<select
				value={value}
				onChange={(e) => onChange?.(e.target.value)}
				className="border-slate-200/90 bg-white/90 focus:border-primary focus:ring-primary/20 h-11 w-full cursor-pointer rounded-xl border px-3 text-sm shadow-sm outline-none focus:ring-4 dark:border-border dark:bg-background"
			>
				{children}
			</select>
		</label>
	);
}

export function VideoConverter() {
	const t = useTranslations("VideoConverter.tool");
	const sourceOptions: ReadonlyArray<{
		value: SourceKind;
		label: string;
		icon: typeof HardDrive;
	}> = [
		{ value: "device", label: t("sources.device"), icon: HardDrive },
		{ value: "dropbox", label: t("sources.dropbox"), icon: Cloud },
		{ value: "google-drive", label: t("sources.googleDrive"), icon: Cloud },
		{ value: "onedrive", label: t("sources.oneDrive"), icon: Cloud },
		{ value: "url", label: t("sources.url"), icon: Link2 },
	];
	const categoryOptions: ReadonlyArray<{
		value: OutputCategory;
		label: string;
		icon: typeof FileVideo;
	}> = [
		{ value: "video", label: t("output.video"), icon: FileVideo },
		{ value: "audio", label: t("output.audio"), icon: FileAudio },
		{ value: "image", label: t("output.image"), icon: FileImage },
	];
	const fileInputRef = useRef<HTMLInputElement>(null);
	const sourceAbortRef = useRef<AbortController | null>(null);
	const [items, setItems] = useState<QueueItem[]>([]);
	const [isDragging, setIsDragging] = useState(false);
	const [activeSource, setActiveSource] = useState<CloudSource | "url" | null>(
		null,
	);
	const [urlValue, setUrlValue] = useState("");
	const [notice, setNotice] = useState<string | null>(null);
	const [isSourceLoading, setIsSourceLoading] = useState(false);
	const [formatOpen, setFormatOpen] = useState(false);
	const [activeCategory, setActiveCategory] = useState<OutputCategory>("video");
	const [selectedCategory, setSelectedCategory] =
		useState<OutputCategory>("video");
	const [selectedFormat, setSelectedFormat] = useState("MP4");
	const [formatQuery, setFormatQuery] = useState("");
	const [advancedOpen, setAdvancedOpen] = useState(false);
	const [keepAudio, setKeepAudio] = useState(true);

	// Advanced settings state
	const [videoCodec, setVideoCodec] = useState("Auto");
	const [quality, setQuality] = useState("Balanced");
	const [resolution, setResolution] = useState("Original");
	const [frameRate, setFrameRate] = useState("Original");
	const [bitrate, setBitrate] = useState("Auto");
	const [sampleRate, setSampleRate] = useState("Original");
	const [channels, setChannels] = useState("Original");
	const [gifSize, setGifSize] = useState("Original");
	const [gifFrameRate, setGifFrameRate] = useState("15 fps");
	const [loop, setLoop] = useState("Forever");

	const [isConverting, setIsConverting] = useState(false);
	const selectedCategoryLabel =
		selectedCategory === "audio"
			? t("output.audio")
			: selectedCategory === "image"
				? t("output.image")
				: t("output.video");

	useEffect(() => {
		if (REMOTE_VIDEO_SOURCES_ENABLED) {
			preloadCloudSourceSdks();
		}
		return () => sourceAbortRef.current?.abort();
	}, []);

	const addImportedVideos = (videos: ImportedVideo[]) => {
		if (videos.length === 0) return;
		setActiveSource(null);
		setItems((current) => {
			const existing = new Set(
				current.map((item) => `${item.name}:${item.size ?? 0}`),
			);
			const additions = videos
				.filter(({ file }) => !existing.has(`${file.name}:${file.size}`))
				.map(({ file, source, sourceLabel }) => ({
					id: crypto.randomUUID(),
					name: file.name,
					size: file.size,
					source,
					sourceLabel,
					file: file,
					status: "idle" as const,
				}));
			return [...current, ...additions];
		});
	};

	const addDeviceFiles = (files: File[]) => {
		setNotice(null);
		addImportedVideos(
			files.map((file) => ({
				file,
				source: "device" as const,
				sourceLabel: t("sources.deviceLabel"),
			})),
		);
	};

	const handleInput = (event: ChangeEvent<HTMLInputElement>) => {
		addDeviceFiles(Array.from(event.target.files ?? []));
		event.target.value = "";
	};

	const handleDrop = (event: DragEvent<HTMLDivElement>) => {
		event.preventDefault();
		setIsDragging(false);
		addDeviceFiles(Array.from(event.dataTransfer.files));
	};

	const selectSource = (source: SourceKind) => {
		setNotice(null);
		if (isSourceLoading) return;
		if (source === "device") {
			fileInputRef.current?.click();
			return;
		}
		setActiveSource(source);
	};

	const cancelSourceImport = () => {
		sourceAbortRef.current?.abort();
		sourceAbortRef.current = null;
		setIsSourceLoading(false);
		setActiveSource(null);
		setNotice(null);
	};

	const connectCloudSource = async (source: CloudSource) => {
		const controller = new AbortController();
		sourceAbortRef.current?.abort();
		sourceAbortRef.current = controller;
		setIsSourceLoading(true);
		const providerName =
			source === "dropbox"
				? "Dropbox"
				: source === "google-drive"
					? "Google Drive"
					: source === "onedrive"
						? "OneDrive"
						: t("sources.providerFallback");
		setNotice(t("cloud.opening", { provider: providerName }));

		try {
			const result =
				source === "dropbox"
					? await importDropboxVideos({ signal: controller.signal })
					: source === "google-drive"
						? await importGoogleDriveVideos({ signal: controller.signal })
						: await importOneDriveVideos({ signal: controller.signal });

			if (result.cancelled) {
				setNotice(null);
				setActiveSource(null);
				return;
			}

			addImportedVideos(result.files);
			setNotice(
				result.failedCount > 0
					? t("cloud.partial", { count: result.files.length, failedCount: result.failedCount })
					: t("cloud.imported", { count: result.files.length, provider: providerName }),
			);
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError") return;
			setNotice(t("cloud.failed"));
		} finally {
			if (sourceAbortRef.current === controller) {
				sourceAbortRef.current = null;
				setIsSourceLoading(false);
			}
		}
	};

	const addUrl = async () => {
		const controller = new AbortController();
		sourceAbortRef.current?.abort();
		sourceAbortRef.current = controller;
		setIsSourceLoading(true);
		setNotice(t("url.reading"));

		try {
			const imported = await importVideoFromUrl({
				value: urlValue,
				signal: controller.signal,
			});
			addImportedVideos([imported]);
			setUrlValue("");
			setNotice(t("cloud.imported", { count: 1, provider: imported.sourceLabel }));
		} catch (error) {
			if (error instanceof DOMException && error.name === "AbortError") return;
			setNotice(t("url.failed"));
		} finally {
			if (sourceAbortRef.current === controller) {
				sourceAbortRef.current = null;
				setIsSourceLoading(false);
			}
		}
	};

	const filteredFormats = useMemo(() => {
		const query = formatQuery.trim().toLowerCase();
		return OUTPUT_FORMATS[activeCategory].filter((format) =>
			format.toLowerCase().includes(query),
		);
	}, [activeCategory, formatQuery]);

	const chooseFormat = ({
		format,
		category,
	}: {
		format: string;
		category: OutputCategory;
	}) => {
		setSelectedFormat(format);
		setSelectedCategory(category);
		setFormatOpen(false);
		setFormatQuery("");
		// Reset all item statuses to idle when changing format
		setItems((current) =>
			current.map((item) => ({
				...item,
				status: "idle" as const,
				progress: undefined,
				error: undefined,
			})),
		);
	};

	const startConversion = async () => {
		if (items.length === 0) return;

		setIsConverting(true);

		// Dynamically import FFmpeg functions and load FFmpeg worker first
		const { loadFFmpeg, convertVideo, convertAudio, convertToGif } =
			await import("@/lib/ffmpeg");

		// Initialize FFmpeg worker before conversion
		try {
			await loadFFmpeg();
		} catch (error) {
			console.error("Failed to load FFmpeg:", error);
			setNotice(t("errors.load"));
			setIsConverting(false);
			return;
		}

		// Filter items with files and idle status
		const itemsToConvert = items.filter(
			(item) => item.file && item.status === "idle",
		);

		for (const item of itemsToConvert) {
			if (!item.file) continue;

			try {
				// Update status to loading
				setItems((current) =>
					current.map((i) =>
						i.id === item.id ? { ...i, status: "loading" as const } : i,
					),
				);

				const options: ConversionOptions = {
					format: selectedFormat,
					videoCodec: videoCodec !== "Auto" ? videoCodec : undefined,
					quality,
					resolution,
					frameRate,
					keepAudio,
					bitrate: bitrate !== "Auto" ? bitrate : undefined,
					sampleRate,
					channels,
					gifSize,
					gifFrameRate,
					loop,
				};

				let outputBlob: Blob;

				if (selectedCategory === "image") {
					outputBlob = await convertToGif({
						file: item.file,
						options,
						onProgress: (progress) => {
							setItems((current) =>
								current.map((i) =>
									i.id === item.id
										? {
												...i,
												status: "converting" as const,
												progress: progress.percent,
											}
										: i,
								),
							);
						},
					});
				} else if (selectedCategory === "audio") {
					outputBlob = await convertAudio({
						file: item.file,
						options,
						onProgress: (progress) => {
							setItems((current) =>
								current.map((i) =>
									i.id === item.id
										? {
												...i,
												status: "converting" as const,
												progress: progress.percent,
											}
										: i,
								),
							);
						},
					});
				} else {
					outputBlob = await convertVideo({
						file: item.file,
						options,
						onProgress: (progress) => {
							setItems((current) =>
								current.map((i) =>
									i.id === item.id
										? {
												...i,
												status: "converting" as const,
												progress: progress.percent,
											}
										: i,
								),
							);
						},
					});
				}

				// Update status to completed
				setItems((current) =>
					current.map((i) =>
						i.id === item.id
							? {
									...i,
									status: "completed" as const,
									progress: 100,
									outputBlob,
								}
							: i,
					),
				);
			} catch (error) {
				console.error("Conversion error:", error);
				setItems((current) =>
					current.map((i) =>
						i.id === item.id
							? {
									...i,
									status: "failed" as const,
								error: t("errors.conversion"),
								}
							: i,
					),
				);
			}
		}

		setIsConverting(false);
	};

	const downloadFile = (item: QueueItem) => {
		if (!item.outputBlob) return;

		const url = URL.createObjectURL(item.outputBlob);
		const a = document.createElement("a");
		a.href = url;

		const extension = selectedFormat.toLowerCase();
		const baseName = item.name.replace(/\.[^/.]+$/, "");
		a.download = `${baseName}.${extension}`;

		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	const downloadAllCompleted = () => {
		const completedItems = items.filter(
			(item) => item.status === "completed" && item.outputBlob,
		);

		if (completedItems.length === 0) return;

		completedItems.forEach((item, index) => {
			setTimeout(() => {
				downloadFile(item);
			}, index * 500);
		});
	};

	const getStatusIcon = (status: ConversionStatus) => {
		switch (status) {
			case "loading":
				return <Loader2 className="size-4 animate-spin text-blue-600" />;
			case "converting":
				return <Loader2 className="size-4 animate-spin text-cyan-600" />;
			case "completed":
				return <Check className="size-4 text-green-600" />;
			case "failed":
				return <X className="size-4 text-red-600" />;
			default:
				return <FileVideo className="size-4" />;
		}
	};

	return (
		<section
			className="bg-background/95 border-blue-100 mx-auto w-full max-w-5xl overflow-hidden rounded-[1.75rem] border shadow-[0_28px_90px_-40px_rgba(2,132,199,0.5)] backdrop-blur-xl dark:border-border dark:bg-background/90"
			aria-label={t("ariaLabel")}
		>
			<div className="space-y-5 bg-gradient-to-br from-slate-50 via-blue-50/55 to-cyan-50/60 p-5 sm:p-7 lg:p-8 dark:bg-none dark:bg-transparent">
				<div
					className={cn(
						"border-blue-200/90 flex min-h-48 flex-col items-center justify-center rounded-2xl border-2 border-dashed bg-gradient-to-br from-white via-white to-blue-50/60 px-5 py-8 text-center shadow-[0_18px_50px_-38px_rgba(30,64,175,0.35)] transition-colors duration-200 dark:border-border dark:bg-none dark:bg-background dark:shadow-none",
						isDragging &&
							"border-primary bg-blue-50 ring-primary/15 ring-4 dark:bg-secondary",
					)}
					onDragEnter={(event) => {
						event.preventDefault();
						setIsDragging(true);
					}}
					onDragOver={(event) => event.preventDefault()}
					onDragLeave={(event) => {
						if (
							!(event.relatedTarget instanceof Node) ||
							!event.currentTarget.contains(event.relatedTarget)
						) {
							setIsDragging(false);
						}
					}}
					onDrop={handleDrop}
				>
					<span className="flex size-14 items-center justify-center rounded-2xl border border-blue-400/40 bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-400 text-white shadow-lg shadow-blue-500/20">
						<UploadCloud className="size-6" aria-hidden="true" />
					</span>
					<p className="mt-4 text-base font-semibold sm:text-lg">
						{t("drop")}
					</p>
					<div className="mt-5 inline-flex overflow-hidden rounded-xl shadow-lg shadow-blue-500/15">
						<label className="focus-within:ring-primary/25 flex h-12 cursor-pointer items-center gap-2 bg-blue-600 px-5 text-sm font-bold text-white transition-colors hover:bg-blue-700 focus-within:ring-4">
							<input
								ref={fileInputRef}
								type="file"
								accept={ACCEPTED_FILE_TYPES}
								multiple
								onChange={handleInput}
								className="sr-only"
							/>
							<FolderOpen className="size-4" aria-hidden="true" />
							{t("choose")}
						</label>
						{REMOTE_VIDEO_SOURCES_ENABLED && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										aria-label={t("chooseSource")}
										className="h-12 w-12 rounded-none border-l border-white/25 bg-blue-600 px-0 text-white hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-white/80"
									>
										<ChevronDown className="size-4" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align="end"
									sideOffset={8}
									className="w-60 rounded-xl p-1.5 shadow-xl"
								>
									{sourceOptions.map((source) => {
										const Icon = source.icon;
										return (
											<DropdownMenuItem
												key={source.value}
												onSelect={() => selectSource(source.value)}
												className="min-h-11 rounded-lg px-3"
												icon={<Icon />}
											>
												{source.label}
											</DropdownMenuItem>
										);
									})}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				</div>

				{REMOTE_VIDEO_SOURCES_ENABLED && activeSource === "url" && (
					<div className="border-blue-200/90 bg-white/85 flex flex-col gap-3 rounded-2xl border p-4 shadow-sm sm:flex-row sm:items-end dark:border-border dark:bg-background">
						<div className="min-w-0 flex-1">
							<label
								htmlFor="video-converter-url"
								className="mb-2 block text-xs font-bold tracking-wide uppercase"
							>
								{t("url.label")}
							</label>
							<Input
								type="url"
								id="video-converter-url"
								value={urlValue}
								onChange={(event) => setUrlValue(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter" && urlValue.trim()) {
										event.preventDefault();
										void addUrl();
									}
								}}
								placeholder="https://example.com/video.mp4"
								size="lg"
								disabled={isSourceLoading}
							/>
						</div>
						<div className="flex gap-2">
							<Button
								variant="outline"
								className="h-11 rounded-xl"
								onClick={cancelSourceImport}
							>
								{t("url.cancel")}
							</Button>
							<Button
								className="h-11 rounded-xl"
								onClick={() => void addUrl()}
								disabled={isSourceLoading || !urlValue.trim()}
							>
								{isSourceLoading ? (
									<Loader2 className="animate-spin" />
								) : (
									<Link2 />
								)}
								{isSourceLoading ? t("url.importing") : t("url.add")}
							</Button>
						</div>
					</div>
				)}

				{REMOTE_VIDEO_SOURCES_ENABLED &&
					activeSource &&
					activeSource !== "url" && (
						<div className="border-blue-200/90 bg-white/85 flex flex-col gap-4 rounded-2xl border p-4 shadow-sm sm:flex-row sm:items-center dark:border-border dark:bg-background">
							<span className="bg-secondary text-secondary-foreground border-secondary-border flex size-11 shrink-0 items-center justify-center rounded-xl border">
								<Cloud className="size-5" aria-hidden="true" />
							</span>
							<p className="min-w-0 flex-1 font-semibold">
								{
									sourceOptions.find((source) => source.value === activeSource)
										?.label
								}
							</p>
							<div className="flex gap-2">
								<Button
									variant="outline"
									className="h-11 rounded-xl"
									onClick={cancelSourceImport}
									aria-label={t("cloud.cancelLabel")}
								>
									<X />
									{t("cloud.cancel")}
								</Button>
								<Button
									className="h-11 rounded-xl"
									onClick={() => void connectCloudSource(activeSource)}
									disabled={isSourceLoading}
								>
									{isSourceLoading && <Loader2 className="animate-spin" />}
									{isSourceLoading ? t("cloud.connecting") : t("cloud.connect")}
								</Button>
							</div>
						</div>
					)}

				{notice && (
					<div
						className="border-blue-200 bg-blue-50/80 text-blue-950 flex items-center gap-3 rounded-xl border px-4 py-3 text-sm dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-100"
						role="status"
					>
						<ShieldCheck className="size-4 shrink-0 text-cyan-600 dark:text-cyan-300" />
						<span className="min-w-0 flex-1">{notice}</span>
						<Button
							variant="ghost"
							size="icon"
							className="size-9 shrink-0 rounded-lg"
							onClick={() => setNotice(null)}
							aria-label={t("noticeDismiss")}
						>
							<X />
						</Button>
					</div>
				)}

				{items.length > 0 && (
					<div className="space-y-2">
						<div className="flex items-center justify-between gap-3">
							<p className="text-sm font-bold">
								{items.length} {t(items.length === 1 ? "queue.video" : "queue.videos")}
							</p>
							<div className="flex gap-2">
								{items.some((i) => i.status === "completed") && (
									<Button
										variant="outline"
										className="h-9 rounded-lg px-2.5"
										onClick={downloadAllCompleted}
									>
										<Download />
										{t("queue.downloadAll")}
									</Button>
								)}
								<Button
									variant="text"
									className="text-muted-foreground h-9 rounded-lg px-2.5"
									onClick={() => setItems([])}
								>
									<Trash2 />
									{t("queue.clearAll")}
								</Button>
							</div>
						</div>
						<ul className="space-y-2" aria-live="polite">
							{items.map((item) => (
								<li
									key={item.id}
									className={cn(
										"border-slate-200/90 bg-white/85 flex items-center gap-3 rounded-xl border p-3 shadow-sm dark:border-border dark:bg-background",
										item.status === "converting" && "ring-2 ring-cyan-500/30",
									)}
								>
									<span
										className={cn(
											"flex size-10 shrink-0 items-center justify-center rounded-lg border",
											item.status === "completed"
												? "bg-green-100 border-green-200 dark:bg-green-950/50 dark:border-green-800"
												: item.status === "failed"
													? "bg-red-100 border-red-200 dark:bg-red-950/50 dark:border-red-800"
													: item.status === "loading" ||
														  item.status === "converting"
														? "bg-blue-100 border-blue-200 dark:bg-blue-950/50 dark:border-blue-800"
														: "bg-secondary text-secondary-foreground border-secondary-border",
										)}
									>
										{getStatusIcon(item.status)}
									</span>
									<div className="min-w-0 flex-1">
										<p className="truncate text-sm font-semibold">
											{item.name}
										</p>
										<div className="flex items-center gap-2 mt-0.5">
											<p className="text-muted-foreground text-xs">
												{item.sourceLabel}
												{item.size !== undefined &&
													` · ${formatBytes(item.size)}`}
											</p>
											{item.status === "converting" &&
												item.progress !== undefined && (
													<span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">
														{item.progress}%
													</span>
												)}
											{item.status === "failed" && item.error && (
												<span
													className="text-xs text-red-600 dark:text-red-400"
													title={item.error}
												>
											{t("queue.failed")}
												</span>
											)}
										</div>
										{(item.status === "loading" ||
											item.status === "converting") &&
											item.progress !== undefined && (
												<div className="mt-2 h-1 bg-gray-200 rounded-full overflow-hidden dark:bg-gray-700">
													<div
														className="h-full bg-cyan-600 transition-all duration-300"
														style={{ width: `${item.progress}%` }}
													/>
												</div>
											)}
									</div>
									{item.status === "completed" && item.outputBlob && (
										<Button
											variant="outline"
											size="icon"
											className="size-10 shrink-0 rounded-lg"
											onClick={() => downloadFile(item)}
									aria-label={t("queue.download", { name: item.name })}
										>
											<Download />
										</Button>
									)}
									{(item.status === "idle" || item.status === "failed") && (
										<Button
											variant="ghost"
											size="icon"
											className="size-10 shrink-0 rounded-lg"
											onClick={() =>
												setItems((current) =>
													current.filter(
														(candidate) => candidate.id !== item.id,
													),
												)
											}
									aria-label={t("queue.remove", { name: item.name })}
										>
											<Trash2 />
										</Button>
									)}
								</li>
							))}
						</ul>
					</div>
				)}

				<div className="space-y-3">
					<p className="text-sm font-bold">{t("output.label")}</p>
					<Popover
						open={formatOpen}
						onOpenChange={(open) => {
							setFormatOpen(open);
							if (open) {
								setActiveCategory(selectedCategory);
								setFormatQuery("");
							}
						}}
					>
						<PopoverTrigger asChild>
							<button
								type="button"
								className="border-blue-200/90 bg-white/90 focus-visible:ring-primary/20 flex min-h-18 w-full cursor-pointer items-center gap-4 rounded-2xl border px-4 py-3 text-left shadow-sm transition-colors hover:border-blue-400 hover:bg-white focus-visible:ring-4 focus-visible:outline-none dark:border-border dark:bg-background dark:hover:bg-secondary/40"
							>
								<span className="flex size-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 text-white shadow-md shadow-blue-500/15">
									{selectedCategory === "audio" ? (
										<FileAudio className="size-5" aria-hidden="true" />
									) : selectedCategory === "image" ? (
										<FileImage className="size-5" aria-hidden="true" />
									) : (
										<FileVideo className="size-5" aria-hidden="true" />
									)}
								</span>
								<span className="min-w-0 flex-1">
									<span className="block text-base font-bold">
										{selectedFormat}
									</span>
									<span className="text-muted-foreground mt-0.5 block text-xs capitalize">
									{t("output.type", { category: selectedCategoryLabel })}
									</span>
								</span>
								<ChevronDown className="text-muted-foreground size-5 shrink-0" />
							</button>
						</PopoverTrigger>
						<PopoverContent
							align="start"
							sideOffset={8}
							className="w-[min(44rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border p-0 shadow-2xl"
						>
							<div className="border-b p-4">
								<div className="relative">
									<Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
									<Input
										value={formatQuery}
										onChange={(event) => setFormatQuery(event.target.value)}
									placeholder={t("output.searchPlaceholder")}
									aria-label={t("output.searchLabel")}
										className="h-11 rounded-xl pl-10"
									/>
								</div>
							</div>
							<div className="grid md:grid-cols-[10rem_1fr]">
								<div className="border-b p-2 md:border-r md:border-b-0">
									<div className="flex gap-1 overflow-x-auto md:flex-col">
									{categoryOptions.map((category) => {
											const Icon = category.icon;
											return (
												<button
													type="button"
													key={category.value}
													onClick={() => {
														setActiveCategory(category.value);
														setFormatQuery("");
													}}
													className={cn(
														"focus-visible:ring-primary/20 flex min-h-11 shrink-0 cursor-pointer items-center gap-2 rounded-lg px-3 text-sm font-semibold transition-colors focus-visible:ring-4 focus-visible:outline-none md:w-full",
														activeCategory === category.value
															? "bg-blue-50 text-blue-800 dark:bg-secondary dark:text-secondary-foreground"
															: "text-muted-foreground hover:bg-accent hover:text-foreground",
													)}
												>
													<Icon className="size-4" aria-hidden="true" />
													{category.label}
												</button>
											);
										})}
									</div>
								</div>
								<div className="max-h-80 overflow-y-auto p-4">
									<div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
										{filteredFormats.map((format) => (
											<button
												type="button"
												key={format}
												onClick={() =>
													chooseFormat({ format, category: activeCategory })
												}
												className={cn(
													"border-border focus-visible:ring-primary/20 relative min-h-11 cursor-pointer rounded-xl border px-3 text-sm font-bold transition-colors hover:border-blue-400 hover:bg-blue-50 focus-visible:ring-4 focus-visible:outline-none dark:hover:bg-secondary",
													selectedCategory === activeCategory &&
														selectedFormat === format
														? "border-blue-500 bg-blue-50 text-blue-800 dark:border-primary dark:bg-secondary dark:text-secondary-foreground"
														: "bg-background",
												)}
											>
												{format}
												{selectedCategory === activeCategory &&
													selectedFormat === format && (
														<Check className="absolute top-1.5 right-1.5 size-3 text-blue-600" />
													)}
											</button>
										))}
									</div>
									{filteredFormats.length === 0 && (
										<div className="border-border text-muted-foreground flex min-h-24 flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-4 text-sm">
											<span>{t("output.empty")}</span>
											<Button
												variant="outline"
												size="sm"
												onClick={() => setFormatQuery("")}
											>
												{t("output.clearSearch")}
											</Button>
										</div>
									)}
								</div>
							</div>
						</PopoverContent>
					</Popover>
				</div>

				<Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
					<CollapsibleTrigger asChild>
						<button
							type="button"
							className="border-slate-200/90 bg-white/85 focus-visible:ring-primary/20 flex min-h-12 w-full cursor-pointer items-center gap-3 rounded-xl border px-4 text-sm font-semibold shadow-sm hover:bg-white focus-visible:ring-4 focus-visible:outline-none dark:border-border dark:bg-background dark:hover:bg-secondary/40"
						>
							<Settings2 className="size-4 text-cyan-600 dark:text-cyan-300" />
							{t("advanced.title")}
							<ChevronDown
								className={cn(
									"text-muted-foreground ml-auto size-4 transition-transform duration-200 motion-reduce:transition-none",
									advancedOpen && "rotate-180",
								)}
							/>
						</button>
					</CollapsibleTrigger>
					<CollapsibleContent>
						<div className="border-slate-200/90 bg-white/70 mt-3 grid gap-4 rounded-2xl border p-4 shadow-sm sm:grid-cols-2 lg:grid-cols-4 dark:border-border dark:bg-background/70">
							{selectedCategory === "video" && (
								<>
									<SettingSelect
										label={t("advanced.videoCodec")}
										value={videoCodec}
										onChange={setVideoCodec}
									>
										<option value="Auto">{t("advanced.auto")}</option>
										<option>H.264</option>
										<option>H.265</option>
										<option>VP9</option>
										<option>AV1</option>
									</SettingSelect>
									<SettingSelect
										label={t("advanced.quality")}
										value={quality}
										onChange={setQuality}
									>
										<option value="Balanced">{t("advanced.balanced")}</option>
										<option value="Best">{t("advanced.best")}</option>
										<option value="Smaller file">{t("advanced.smallerFile")}</option>
									</SettingSelect>
									<SettingSelect
										label={t("advanced.resolution")}
										value={resolution}
										onChange={setResolution}
									>
										<option value="Original">{t("advanced.original")}</option>
										<option>4K</option>
										<option>1080p</option>
										<option>720p</option>
										<option>480p</option>
									</SettingSelect>
									<SettingSelect
										label={t("advanced.frameRate")}
										value={frameRate}
										onChange={setFrameRate}
									>
										<option value="Original">{t("advanced.original")}</option>
										<option>60 fps</option>
										<option>30 fps</option>
										<option>24 fps</option>
									</SettingSelect>
									<div className="border-border flex min-h-11 items-center justify-between rounded-xl border px-3 sm:col-span-2 lg:col-span-4">
										<label
											htmlFor="keep-audio"
											className="cursor-pointer text-sm font-semibold"
										>
											{t("advanced.keepAudio")}
										</label>
										<Switch
											id="keep-audio"
											checked={keepAudio}
											onCheckedChange={setKeepAudio}
										/>
									</div>
								</>
							)}
							{selectedCategory === "audio" && (
								<>
									<SettingSelect
										label={t("advanced.bitrate")}
										value={bitrate}
										onChange={setBitrate}
									>
										<option value="Auto">{t("advanced.auto")}</option>
										<option>320 kbps</option>
										<option>256 kbps</option>
										<option>192 kbps</option>
										<option>128 kbps</option>
									</SettingSelect>
									<SettingSelect
										label={t("advanced.sampleRate")}
										value={sampleRate}
										onChange={setSampleRate}
									>
										<option value="Original">{t("advanced.original")}</option>
										<option>48 kHz</option>
										<option>44.1 kHz</option>
										<option>32 kHz</option>
									</SettingSelect>
									<SettingSelect
										label={t("advanced.channels")}
										value={channels}
										onChange={setChannels}
									>
										<option value="Original">{t("advanced.original")}</option>
										<option value="Stereo">{t("advanced.stereo")}</option>
										<option value="Mono">{t("advanced.mono")}</option>
									</SettingSelect>
								</>
							)}
							{selectedCategory === "image" && (
								<>
									<SettingSelect
										label={t("advanced.gifSize")}
										value={gifSize}
										onChange={setGifSize}
									>
										<option value="Original">{t("advanced.original")}</option>
										<option>1080p</option>
										<option>720p</option>
										<option>480p</option>
									</SettingSelect>
									<SettingSelect
										label={t("advanced.frameRate")}
										value={gifFrameRate}
										onChange={setGifFrameRate}
									>
										<option>15 fps</option>
										<option>24 fps</option>
										<option>12 fps</option>
										<option>10 fps</option>
									</SettingSelect>
									<SettingSelect label={t("advanced.loop")} value={loop} onChange={setLoop}>
										<option value="Forever">{t("advanced.forever")}</option>
										<option value="Once">{t("advanced.once")}</option>
										<option value="3 times">{t("advanced.threeTimes")}</option>
									</SettingSelect>
								</>
							)}
						</div>
					</CollapsibleContent>
				</Collapsible>

				<Button
					className="h-12 w-full rounded-xl text-base"
					disabled={items.length === 0 || isConverting}
					onClick={startConversion}
				>
					{isConverting ? (
						<>
							<Loader2 className="size-4 animate-spin" />
							{t("actions.converting")}
						</>
					) : (
						<>
							<FileVideo />
							{items.length === 0
								? t("actions.choose")
								: t("actions.convert", {
										count: items.length,
										unit: t(items.length === 1 ? "queue.video" : "queue.videos"),
										format: selectedFormat,
									})}
						</>
					)}
				</Button>
			</div>
		</section>
	);
}
